import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Circle, Marker, Popup, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { feetToMeters } from "../../lib/utils";

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const createNumberedIcon = (number, color = "#0F172A") => {
  return L.divIcon({
    className: "custom-marker",
    html: `<div style="
      background-color: ${color};
      color: white;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      font-size: 14px;
      border: 3px solid white;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    ">${number}</div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
};

const guardIcon = L.divIcon({
  className: "guard-marker",
  html: `<div style="
    background-color: #F97316;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 3px solid white;
    box-shadow: 0 0 10px rgba(249, 115, 22, 0.6);
  "></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

const MapClickHandler = ({ onMapClick }) => {
  useMapEvents({
    click: (e) => {
      if (onMapClick) {
        onMapClick(e.latlng);
      }
    },
  });
  return null;
};

const FitBounds = ({ checkpoints }) => {
  const map = useMap();
  
  useEffect(() => {
    if (checkpoints && checkpoints.length > 0) {
      const bounds = L.latLngBounds(
        checkpoints.map((cp) => [cp.latitude, cp.longitude])
      );
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [checkpoints, map]);
  
  return null;
};

const FocusOnSelectedCheckpoint = ({ selectedCheckpoint, checkpoints }) => {
  const map = useMap();
  
  useEffect(() => {
    if (selectedCheckpoint) {
      const checkpointLatLng = L.latLng(selectedCheckpoint.latitude, selectedCheckpoint.longitude);
      const bounds = map.getBounds();
      
      // Verificar si el checkpoint está visible en el viewport actual
      const isVisible = bounds.contains(checkpointLatLng);
      
      if (!isVisible) {
        // Si no está visible, centrar el mapa en el checkpoint con zoom 15
        map.setView(checkpointLatLng, 15, { animate: true });
      }
    }
  }, [selectedCheckpoint, map]);
  
  return null;
};

export const CheckpointMap = ({
  checkpoints = [],
  selectedCheckpoint,
  onCheckpointClick,
  onMapClick,
  guardPosition,
  showRadius = true,
  height = "400px",
  interactive = true,
  completedCheckpoints = [],
}) => {
  const [mapCenter, setMapCenter] = useState([18.478973, -69.967669]); // República Dominicana default
  const mapRef = useRef(null);

  useEffect(() => {
    if (checkpoints.length > 0) {
      const avgLat = checkpoints.reduce((sum, cp) => sum + cp.latitude, 0) / checkpoints.length;
      const avgLng = checkpoints.reduce((sum, cp) => sum + cp.longitude, 0) / checkpoints.length;
      setMapCenter([avgLat, avgLng]);
    } else if (guardPosition) {
      setMapCenter([guardPosition.latitude, guardPosition.longitude]);
    }
  }, [checkpoints, guardPosition]);

  return (
    <div className="rounded-lg overflow-hidden border border-slate-200" style={{ height }}>
      <MapContainer
        center={mapCenter}
        zoom={15}
        style={{ height: "100%", width: "100%" }}
        ref={mapRef}
        scrollWheelZoom={interactive}
        dragging={interactive}
        zoomControl={interactive}
      >
        <TileLayer
          attribution='&copy; <a href="https://carto.com/">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
        />
        
        {interactive && onMapClick && <MapClickHandler onMapClick={onMapClick} />}
        {checkpoints.length > 0 && <FitBounds checkpoints={checkpoints} />}
        {selectedCheckpoint && <FocusOnSelectedCheckpoint selectedCheckpoint={selectedCheckpoint} checkpoints={checkpoints} />}
        
        {checkpoints.map((checkpoint, index) => {
          const isCompleted = completedCheckpoints.includes(checkpoint.id);
          const isSelected = selectedCheckpoint?.id === checkpoint.id;
          
          // Determinar el color: verde si está completado, naranja si está seleccionado, negro por defecto
          let markerColor = "#0F172A"; // Negro por defecto
          if (isCompleted) {
            markerColor = "#10B981"; // Verde para completados
          } else if (isSelected) {
            markerColor = "#F97316"; // Naranja para seleccionado
          }
          
          return (
            <div key={checkpoint.id}>
              {showRadius && (
                <Circle
                  center={[checkpoint.latitude, checkpoint.longitude]}
                  radius={feetToMeters(checkpoint.radius_feet)}
                  pathOptions={{
                    color: markerColor,
                    fillColor: markerColor,
                    fillOpacity: 0.1,
                    weight: 2,
                  }}
                />
              )}
              <Marker
                position={[checkpoint.latitude, checkpoint.longitude]}
                icon={createNumberedIcon(
                  index + 1,
                  markerColor
                )}
                eventHandlers={{
                  click: () => onCheckpointClick && onCheckpointClick(checkpoint),
                }}
              >
                <Popup>
                  <div className="p-2">
                    <h3 className="font-bold text-slate-900">{checkpoint.name}</h3>
                    <p className="text-sm text-slate-600">{checkpoint.description}</p>
                    {isCompleted && (
                      <p className="text-xs text-green-600 font-semibold mt-1">
                        ✓ Completado
                      </p>
                    )}
                    <p className="text-xs font-mono text-slate-500 mt-1">
                      {checkpoint.latitude.toFixed(6)}, {checkpoint.longitude.toFixed(6)}
                    </p>
                    <p className="text-xs text-slate-500">
                      Radio: {checkpoint.radius_feet} pies
                    </p>
                  </div>
                </Popup>
              </Marker>
            </div>
          );
        })}
        
        {guardPosition && (
          <Marker
            position={[guardPosition.latitude, guardPosition.longitude]}
            icon={guardIcon}
          >
            <Popup>
              <div className="p-2">
                <h3 className="font-bold text-orange-600">Tu Ubicación</h3>
                <p className="text-xs font-mono text-slate-500">
                  {guardPosition.latitude.toFixed(6)}, {guardPosition.longitude.toFixed(6)}
                </p>
              </div>
            </Popup>
          </Marker>
        )}
      </MapContainer>
    </div>
  );
};
